'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  addressFormSchema,
  resolveCatchMessage,
  storeConfigSelector,
  TAddressFormValues,
  TCatchMessage,
  TStoreConfig,
  useCountries,
  useLoadingContext,
  useToastContext,
  useValidateCustomerAddressMutation,
} from '@segway/core'
import {
  CartAddressInput,
  CountryCodeEnum,
  CustomerAddressInput,
} from '@segway/core/src/graphql/generated/graphql'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { useForm, useWatch } from 'react-hook-form'

import { AddressFieldComparison, compareAddresses } from '@/app/[locale]/customer/address/_utils'

/**
 * 用户地址表单 Hooks
 */

const phoneUtil = PhoneNumberUtil.getInstance()
const useUserAddressForm = (
  onSubmit: (values: CartAddressInput & { default?: boolean }) => void,
  defaultValues: (CartAddressInput & { default_shipping?: boolean }) | null | undefined,
) => {
  // storeConfig
  const storeConfig = useAppSelector(storeConfigSelector)
  // create schema
  const schema = useMemo(
    () => addressFormSchema(phoneUtil, storeConfig as TStoreConfig),
    [storeConfig],
  )

  const form = useForm<TAddressFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues
      ? {
          firstname: defaultValues?.firstname ?? '',
          lastname: defaultValues?.lastname ?? '',
          company: defaultValues?.company ?? '',
          street: [
            defaultValues?.street?.[0] ?? '',
            defaultValues?.street?.[1] ?? '',
            defaultValues?.street?.[2] ?? '',
          ],
          city: defaultValues?.city ?? '',
          region: defaultValues?.region ?? '',
          // region_id 在 useEffect 中异步设置
          // region_id: String(defaultValues?.region_id),
          postcode: defaultValues?.postcode ?? '',
          country_code: defaultValues?.country_code ?? process.env.NEXT_PUBLIC_COUNTRY_CODE,
          telephone: defaultValues?.telephone ?? '',
          save_in_address_book: defaultValues?.save_in_address_book ?? false,
          default: defaultValues?.default_shipping ?? false, // TODO: 是否考虑default_billing
        }
      : {
          firstname: '',
          lastname: '',
          company: '',
          street: ['', '', ''],
          city: '',
          region: '',
          postcode: '',
          telephone: '',
          country_code: process.env.NEXT_PUBLIC_COUNTRY_CODE,
          save_in_address_book: false,
          default: false,
        },
  })

  const loading = useLoadingContext()
  const toast = useToastContext()
  const [validateCustomerAddressMutation] = useValidateCustomerAddressMutation()

  const [addressValidator, setAddressValidator] = useState<{
    modalVisible: boolean
    address: TAddressFormValues | null
    improvedAddress?: CustomerAddressInput | null
    addressComparison?: AddressFieldComparison[] | null
    saveCallback?: (address: CustomerAddressInput) => void
    errorMessage?: string
  }>({
    modalVisible: false,
    address: null,
    improvedAddress: null,
    addressComparison: null,
    saveCallback: () => {},
    errorMessage: '',
  })

  // 使用 useWatch 监听表单 region_id 数据变化
  const regionId = useWatch({
    control: form.control,
    name: 'region_id',
  })

  // 使用 useWatch 监听表单 country_code 数据变化
  const countryCode = useWatch({
    control: form.control,
    name: 'country_code',
  })

  const {
    countriesOptions,
    setRegionsByCountryId,
    regionsOptions,
    getRegionCodeById,
    getRegionNameById,
  } = useCountries()

  const closeAddressValidator = useCallback(() => {
    setAddressValidator({
      ...addressValidator,
      modalVisible: false,
    })
  }, [addressValidator])

  const formSubmit = useCallback(
    async (values: TAddressFormValues) => {
      try {
        if (!values) {
          return
        }
        // 地址校验
        loading.show()
        const address = {
          city: values?.city ?? '',
          company: values?.company ?? '',
          country_id: values?.country_code as CountryCodeEnum,
          country_code: values?.country_code as CountryCodeEnum,
          firstname: values?.firstname ?? '',
          lastname: values?.lastname ?? '',
          postcode: values?.postcode ?? '',
          region: {
            region_id: Number(values?.region_id),
            region: getRegionNameById(Number(values?.region_id)), // TODO
          },
          street: values.street,
          telephone: values?.telephone ?? '',
        }
        const validateRes = await validateCustomerAddressMutation({
          input: address,
        })
        // 地址校验失败, 弹窗提示
        if (validateRes?.error) {
          setAddressValidator({
            address: values,
            modalVisible: true,
            errorMessage: resolveCatchMessage(validateRes?.error as TCatchMessage),
          })
          loading.hide()
        } else {
          // 地址校验成功，对比地址
          const curAddress: CustomerAddressInput = { ...address }
          curAddress.country_id = address.country_code
          delete curAddress.country_code
          const improvedAddress = validateRes?.data?.validateAddress as CustomerAddressInput
          if (improvedAddress) {
            const addressComparison = compareAddresses(
              curAddress,
              improvedAddress as CustomerAddressInput,
            )
            // 判断是否有推荐的地址
            const isAddressNeedToImprove = addressComparison?.some(
              (addressItem) => addressItem.isDifferent,
            )
            // 有推荐的地址, 弹窗展示
            if (isAddressNeedToImprove) {
              setAddressValidator({
                address: values, // Form values
                improvedAddress,
                addressComparison,
                modalVisible: true,
                // 选择地址后的回调，返回选择的地址 addressSelected
                saveCallback: (addressSelected: CustomerAddressInput) => {
                  const query = {
                    ...values,
                    city: addressSelected?.city ?? '',
                    company: addressSelected?.company ?? '',
                    country_code: addressSelected?.country_code as CountryCodeEnum,
                    firstname: addressSelected?.firstname ?? '',
                    lastname: addressSelected?.lastname ?? '',
                    postcode: addressSelected?.postcode ?? '',
                    region: getRegionCodeById(Number(addressSelected?.region?.region_id)),
                    region_id: addressSelected?.region?.region_id,
                    telephone: addressSelected?.telephone ?? '',
                    street: [
                      addressSelected?.street?.[0] ?? '',
                      addressSelected?.street?.[1] ?? '',
                      addressSelected?.street?.[2] ?? '',
                    ],
                  }
                  // 设置表单，国家不重置
                  form.reset({
                    city: addressSelected?.city ?? '',
                    company: addressSelected?.company ?? '',
                    firstname: addressSelected?.firstname ?? '',
                    lastname: addressSelected?.lastname ?? '',
                    postcode: addressSelected?.postcode ?? '',
                    region: getRegionCodeById(Number(addressSelected?.region?.region_id)),
                    region_id: String(addressSelected?.region?.region_id),
                    telephone: addressSelected?.telephone ?? '',
                    street: [
                      addressSelected?.street?.[0] ?? '',
                      addressSelected?.street?.[1] ?? '',
                      addressSelected?.street?.[2] ?? '',
                    ],
                  })
                  // 提交表单
                  onSubmit(query)
                },
              })
              loading.hide()
            } else {
              // 地址一致，提交表单
              onSubmit({
                ...values,
                region_id: Number(values.region_id),
                street: [values.street[0], values.street[1], values?.street?.[2] || ''],
              })
            }
          } else {
            toast.show({
              icon: 'fail',
              content: 'Validate address failed',
            })
            loading.hide()
          }
        }
      } catch (e) {
        loading.hide()
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
      }
    },
    [
      form,
      getRegionCodeById,
      getRegionNameById,
      loading,
      onSubmit,
      toast,
      validateCustomerAddressMutation,
    ],
  )

  useEffect(() => {
    if (regionId) {
      form.setValue('region', getRegionCodeById(Number(regionId)))
    }
  }, [regionId, form, getRegionCodeById])

  useEffect(() => {
    if (countryCode) {
      setRegionsByCountryId(countryCode)
      // TODO test: 如果没有省份列表，测试省份输入框
      form.reset({
        country_code: countryCode,
        region: '',
        region_id: '',
      })
    }
  }, [countryCode, form, setRegionsByCountryId])

  useEffect(() => {
    // 设表单默认值
    if (defaultValues) {
      setRegionsByCountryId(defaultValues?.country_code ?? process.env.NEXT_PUBLIC_COUNTRY_CODE)
      setTimeout(() => {
        form.setValue('region_id', String(defaultValues?.region_id))
      }, 200)
    } else {
      setRegionsByCountryId(process.env.NEXT_PUBLIC_COUNTRY_CODE ?? 'MX')
    }
  }, [defaultValues, form, setRegionsByCountryId])

  return {
    form,
    formSubmit,
    countriesOptions,
    regionsOptions,
    addressValidator,
    closeAddressValidator,
  }
}

export default useUserAddressForm
